import { useMemo } from "react";

import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { sharedEntityListForUserQueryDocument } from "./team.gql";

export interface SharedEntity {
  entityId: string;
  entityType: string;
  entityName: string;
  right: string;
  teamId: string;
  teamName: string;
  teamColor?: string | null;
  teamLogo?: string | null;
  teamLogoType?: string | null;
}

export interface SharedTeamGroup {
  teamId: string;
  teamName: string;
  teamColor?: string | null;
  teamLogo?: string | null;
  teamLogoType?: string | null;
  entityList: {
    entityId: string;
    entityType: string;
    entityName: string;
    right: string;
  }[];
}

export interface UseSharedEntityListProps {
  workspaceId?: string;
  enabled?: boolean;
}

export const useSharedEntityList = ({ workspaceId, enabled = true }: UseSharedEntityListProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.teams.sharedList(workspaceId ?? ""),
    queryFn: async () => {
      const client = createOperationClient("sharedEntityListForUser");
      return await client.request(sharedEntityListForUserQueryDocument, { input: workspaceId ?? "" });
    },
    enabled: !!workspaceId && enabled,
  });

  const sharedEntityList: SharedEntity[] = useMemo(() => {
    if (!queryResult.data) {
      return [];
    }
    return queryResult.data.sharedEntityListForUser;
  }, [queryResult.data]);

  const sharedTeamGroupList: SharedTeamGroup[] = useMemo(() => {
    const groupMap = new Map<string, SharedTeamGroup>();
    for (const entity of sharedEntityList) {
      if (entity.right === "NONE") continue;
      let group = groupMap.get(entity.teamId);
      if (!group) {
        group = {
          teamId: entity.teamId,
          teamName: entity.teamName,
          teamColor: entity.teamColor,
          teamLogo: entity.teamLogo,
          teamLogoType: entity.teamLogoType,
          entityList: [],
        };
        groupMap.set(entity.teamId, group);
      }
      group.entityList.push({
        entityId: entity.entityId,
        entityType: entity.entityType,
        entityName: entity.entityName,
        right: entity.right,
      });
    }
    const groupList = [...groupMap.values()];
    groupList.sort((a, b) => a.teamName.localeCompare(b.teamName));
    for (const group of groupList) {
      group.entityList.sort((a, b) => a.entityName.localeCompare(b.entityName));
    }
    return groupList;
  }, [sharedEntityList]);

  return {
    sharedEntityList,
    sharedTeamGroupList,
    loading: queryResult.isLoading,
    error: queryResult.error,
    queryResult,
  };
};
